/**
 * The plan editor's in-memory document: a flat list of elements expressed in
 * the *frame* asset's coordinates (mm, Y-down), each remembering which
 * representation and which asset it came from.
 *
 * Loading composes every entry's transform in (the `/plan/scene` endpoint has
 * already done the chain walk); saving inverts it back per representation, so
 * the user edits one coherent drawing while the storage stays per-asset
 * (spec 2026-08-22-01 §1, spec 2026-08-22-02 §3/§4).
 *
 * Pure TypeScript — no React, no DOM.
 */
import {
    IDENTITY,
    applyTransform,
    bboxOfPoints,
    distanceToRing,
    invertTransform,
    pointInPolygon,
    projectToLine,
    ringInside,
    ringTolerance,
    ringsDisjoint,
    shoelace,
    type BBox,
    type Point,
    type Transform,
} from './geometry'
import {
    deltaTransform,
    mmPerUnit,
    mmToUnits,
    normalizeAngle,
    type AnchorPair,
    type FrameCalibration,
} from './calibrate'
import {
    DEFAULT_WALL_THICKNESS,
    defaultWidth,
    isWindowKind,
    type OpeningKind,
    type Operation,
    type Swing,
} from './openings'
import type { components } from '../../../api/schema'
import { lotBaseTypeCode } from '../../../lib/asset-type-tree'
import { PLAN_COLORS } from './palette'

export type PlanScene = components['schemas']['PlanScene']
export type PlanSceneEntry = components['schemas']['PlanSceneEntry']
export type PlanSymbol = components['schemas']['PlanSymbol']
export type VectorValue = components['schemas']['VectorRepresentationValue']
/** One entry of the organization's network catalogue (the `network_kind` enum). */
export type PlanNetwork = components['schemas']['PlanNetwork']

/**
 * The catalogue is organization-editable ontology data, and its colour and
 * dash end up in SVG attributes and inline styles. Same whitelist as the Go
 * renderer (`internal/svgplan/runs.go`): anything else falls back to the
 * neutral run colour, or to no dash.
 */
const SAFE_NETWORK_COLOR = /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]{3,20}|rgba?\([0-9., %]{5,40}\))$/
const SAFE_NETWORK_DASH = /^[0-9]+(\.[0-9]+)?([ ,]+[0-9]+(\.[0-9]+)?)*$/

export function sanitizeNetworks(list: PlanNetwork[] | null | undefined): PlanNetwork[] {
    return (list ?? []).map((n) => {
        const { dash, ...rest } = n
        return {
            ...rest,
            color: SAFE_NETWORK_COLOR.test(n.color ?? '') ? n.color : PLAN_COLORS.runFallback,
            ...(dash && SAFE_NETWORK_DASH.test(dash) ? { dash } : {}),
        }
    })
}

/** Where a digitized bundle's geometry came from (spec 2026-09-03-01). */
export type VectorProvenance = NonNullable<VectorValue['provenance']>

export type ElementKind = 'wall' | 'space' | 'opening' | 'equipment' | 'run' | 'riser'

/** A per-element asset that does not exist yet and will be created on save. */
export interface PendingAsset {
    key: string
    name: string
    typeCode: string
    /**
     * The asset's public code — `B02`. Stored as an external id under the
     * `code` source, not a column (spec 2026-08-30-03 §3), and optional: a lot
     * is identified by its name, which is what uniqueness is enforced on.
     */
    code?: string
    /**
     * `unit_kind` for a pending grouper — the ontology's mandatory "physical
     * nature" enum (`apartment | parking | cellar | …`). What separates a flat
     * from a cellar; the asset type only separates units from common areas.
     */
    unitKind?: string
    /**
     * `room_number` for a pending room — the 2 of "Chambre 2" (spec
     * 2026-08-31-02 §1). Written as a property value on create, like
     * `unitKind`, and only on the room types whose ontology declares it.
     */
    roomNumber?: number
    parentUid?: string
    /**
     * The *pending* asset this one parents to, when the containing space is
     * itself being created by this save (spec 2026-08-25-04 §2: a radiator
     * dropped in a room drawn seconds ago parents to that room). The save
     * orchestrator resolves it to a uid once the parent has been created.
     */
    parentKey?: string
    /** True when the parent-resolution rule (§6) had to fall back to the frame asset. */
    parentFallback?: boolean
    /**
     * True when NO ancestor of the drop point has a type the ontology lets
     * this one hang from, so the API would refuse the write (422 since spec
     * 2026-09-18-01).
     *
     * Blocking, and literally so: `computeDiff` reports such an asset in
     * `PlanDiff.refusedAssets` instead of emitting a creation op, and the page
     * refuses the whole save while any is present — on top of the
     * `parent_ontology_refused` validation issue that flags the element.
     */
    parentRefused?: boolean
    /** True when the user overrode the resolved parent in the inspector (§2). */
    parentOverridden?: boolean
    /**
     * `network_kind` of a pending network asset — the network a run's asset
     * carries (spec 2026-10-20-33), written as a property value on create.
     */
    networkKind?: string
}

/**
 * The unit or common area a drawn space **belongs to** (spec 2026-08-30-01 §3).
 *
 * Membership is an aggregating `group_member` edge, never tree parenthood: the tree
 * keeps meaning physical containment, which is what the frame model reads, and
 * a room stays a child of its floor whatever flat it is filed under. One space
 * has at most one grouper, so assigning a new one *replaces* the previous edge
 * — `relationUid` is the row that has to go.
 */
export interface GrouperRef {
    /** The grouper asset, when it already exists. */
    assetUid?: string
    /** The pending grouper's key, when this same save creates it. */
    assetKey?: string
    name?: string
    /** `unit` or `common_area`. */
    typeCode?: string
    /** The live `group_member` row this reflects — absent for a fresh assignment. */
    relationUid?: string
}

export interface ElementOwner {
    assetUid?: string
    assetName?: string
    assetTypeCode?: string
    /**
     * The existing asset's code and `unit_kind`, stamped onto the doc when the
     * scene loads so the inspector can show them and the diff can tell an edit
     * from the value it started at (spec 2026-08-30-03 §4). Both are `''` /
     * undefined when the asset carries none.
     */
    assetCode?: string
    assetUnitKind?: string
    /**
     * A rename typed in the inspector, waiting to be written to the asset
     * (spec 2026-08-30-03 §12). Deliberately **not** `assetName`: that one is
     * rewritten by things that are not edits — promoting an element out of a
     * bundle and demoting it back rewrites owners without touching an asset —
     * so a field only this panel writes is what makes "changed" mean changed.
     */
    assetNameEdit?: string
    /**
     * A room sub-type picked in the inspector (spec 2026-08-31-01 §6). Written
     * alongside `assetTypeCode` — the drawing and the parent rule must see the
     * new type at once — but kept as a separate field for the same reason
     * `assetNameEdit` is: `assetTypeCode` moves when an element is promoted out
     * of a bundle and demoted back, without an asset ever being touched, so it
     * cannot tell an edit from a rewrite.
     */
    assetTypeEdit?: string
    /**
     * The room's **stored** number, stamped onto the doc when the scene loads —
     * the counterpart of `assetCode` / `assetUnitKind` for a grouper (spec
     * 2026-08-31-02 §5). `assetRoomNumberEdit` is what the panel writes, so an
     * untouched room diffs to nothing.
     */
    assetRoomNumber?: number
    assetRoomNumberEdit?: number
    /**
     * The asset's stored name is the server's own derivation — the `auto_name`
     * flag (spec 2026-08-31-02 §2). The editor then reads the room as
     * `{type} {number}` from the *current* type and number rather than from
     * `assetName`, so retyping or renumbering it in the panel moves the label
     * at once, and a save sends no name at all: the server re-derives its own.
     */
    assetAutoName?: boolean
    assetParentUid?: string
    /**
     * A re-parent asked for in the inspector (§2/§6), and the only thing that
     * makes the diff write one. `assetParentUid` alone cannot say it: it is
     * also filled in late by `hydrateOwnerNames` for assets a bundle depicts,
     * which the diff used to read as a re-parent to the parent already stored.
     */
    assetParentEdit?: string
    newAsset?: PendingAsset
    /** The space's unit / common area (spaces only). */
    grouper?: GrouperRef
    /**
     * The asset's implicit frame is **not** the edited one — its footprint on
     * this layer therefore needs an explicit cross-layer registration row
     * (spec 2026-08-25-03 §5, spec 2026-08-25-04 §1). Set by the shell when an
     * element is linked to an asset living outside this frame's subtree; the
     * asset itself is never reparented.
     */
    crossLayer?: boolean
}

export interface SourceRef {
    /** Undefined for geometry the editor has drawn but not saved yet. */
    repUid?: string
    registrationUid?: string
    /** The composed representation → frame transform. Identity for new geometry. */
    transform: Transform
    /** The element's id inside the representation's value. */
    localId: string
    /**
     * The asset the *representation* belongs to, which stops being the
     * element's own owner as soon as a bundle depicts several assets
     * (spec 2026-08-22-03 §4: a demoted element keeps pointing at the asset it
     * depicts through `properties.asset_uid`).
     */
    repOwner?: ElementOwner
    /**
     * The element is inherited **context**, not the subject of the view: it
     * came from an entry the server marked `context` in an asset-centric scene
     * (spec 2026-09-17-01) — an ancestor's geometry, or geometry no asset owns.
     * The renderer paints it quieter so a reader can tell what they opened from
     * what merely surrounds it.
     *
     * Never set in the editor, which is always frame-rooted and never asks for
     * an ancestor's geometry.
     */
    context?: boolean
}

interface ElementBase {
    key: string
    source: SourceRef
    owner: ElementOwner
}

export interface WallElement extends ElementBase {
    kind: 'wall'
    start: Point
    end: Point
    thickness: number
}

export interface SpaceElement extends ElementBase {
    kind: 'space'
    name: string
    boundary: Point[]
    /**
     * What the space goes around without covering it — a pillar, a shaft, a
     * core of toilets (spec 2026-10-19-07). Interior rings, as in GeoJSON:
     * each one inside `boundary` and clear of the others. Absent rather than
     * empty when there are none, so a space that never had one serializes
     * exactly as before.
     */
    holes?: Point[][]
    /**
     * The asset type a digitization proposes for this space, read off
     * `properties.type_code` (spec 2026-09-03-01 decision 7). A *hint*, not an
     * owner: the space still belongs to whatever bundle holds it until somebody
     * promotes it, and promotion is what turns the hint into a real type.
     */
    typeHint?: string
    /**
     * The model's own space type (`properties.space_type`) — the experiment's
     * twenty-value vocabulary the `type_code` above was mapped from. Carried so
     * the first editor save does not erase what the model actually said, which
     * is the only way to tell a mis-mapped room from a mis-read one.
     */
    spaceTypeHint?: string
}

export interface OpeningElement extends ElementBase {
    kind: 'opening'
    openingKind: OpeningKind
    center: Point
    directionDeg: number
    width: number
    wallThickness: number
    operation?: Operation
    swing?: Swing
    leaves?: 1 | 2
    rotation?: 0 | 180
    /** Cross-representation pointer at the host wall. */
    wallRef?: { assetUid: string; wallId: string }
    /** In-document link to the host wall element (editor-time sync). */
    wallKey?: string
}

export interface EquipmentElement extends ElementBase {
    kind: 'equipment'
    position: Point
    rotationDeg: number
    width: number
    depth: number
    /** The asset-type code driving symbol lookup. */
    typeCode?: string
    /**
     * The equipment kind a digitization read off the plan ("radiator",
     * "sink", …). Kinds do not map onto asset types unambiguously, so this
     * stays a hint the user resolves through the type picker (spec
     * 2026-09-03-01 §F5).
     */
    kindHint?: string
}

/**
 * One run of a building network — a duct, a pipe, a cable tray — as an open
 * polyline. It is never a wall: it cuts nothing, bounds no space and has no
 * thickness, only an optional real-world width (`widthMm`, millimetres
 * whatever the frame's plan units). `network` is a code of the ontology's
 * `network_kind` catalogue (`SceneDoc.networks`).
 *
 * A run lives in a bundle — the frame's structure bundle, like the walls —
 * and **depicts** the network asset it is linked to through `owner`, the
 * same split a demoted element has (spec 2026-08-22-03 §4): linking a run to
 * the building's duct network must not move it into a representation of its
 * own on that asset.
 */
export interface RunElement extends ElementBase {
    kind: 'run'
    network: string
    points: Point[]
    widthMm?: number
    /**
     * The level the run's network asset is filed at, when the run tool or the
     * inspector chose one (spec 2026-10-20-33). Never saved: once the run is
     * linked, the asset's parent says it.
     */
    level?: NetworkLevel
}

/** Where a riser's column starts, crosses or ends (spec 2026-10-19-03 §1). */
export type RiserPassage = 'origin' | 'through' | 'terminal'

/**
 * Where a vertical run — a downpipe, a dry riser, a shaft — crosses this floor:
 * a point, never a polyline. Like a run it lives in the frame's structure
 * bundle and **depicts** its column through `owner`: every riser linked to the
 * same asset on the building's floors is one column. `diameterMm` is the real
 * section (millimetres, whatever the plan units), `passage` absent means the
 * stack derives it.
 */
export interface RiserElement extends ElementBase {
    kind: 'riser'
    network: string
    position: Point
    diameterMm?: number
    passage?: RiserPassage
    /** The free `properties.label` ("EP 2"), carried so a save keeps it. */
    label?: string
}

/** Where a run's network asset hangs: its floor, its building or its site. */
export type NetworkLevel = 'floor' | 'building' | 'site'

/**
 * The `asset_uid` a run carries while the network asset it depicts is still
 * pending, resolved by the save once that asset exists.
 */
export const PENDING_LINK_PREFIX = 'pending:'

/**
 * Swaps every pending run link of a value for the uid the save created, and
 * drops one whose asset failed: a run is never saved pointing at a key.
 */
export function resolvePendingRunLinks(value: VectorValue, created: Record<string, string>): VectorValue {
    const pending = (r: { properties?: Record<string, unknown> | null }) =>
        String(r.properties?.asset_uid ?? '').startsWith(PENDING_LINK_PREFIX)
    if (!value.runs?.some(pending)) return value
    return {
        ...value,
        runs: value.runs.map((r) => {
            if (!pending(r)) return r
            const { asset_uid: link, ...rest } = r.properties ?? {}
            const uid = created[String(link).slice(PENDING_LINK_PREFIX.length)]
            const properties = uid ? { ...rest, asset_uid: uid } : rest
            const { properties: _old, ...run } = r
            return Object.keys(properties).length ? { ...run, properties } : run
        }),
    }
}

export type EditorElement = WallElement | SpaceElement | OpeningElement | EquipmentElement | RunElement | RiserElement

export interface UnderlayEntry {
    key: string
    repUid: string
    registrationUid?: string
    assetUid?: string
    assetName?: string
    transform: Transform
    /** The raster's kind (`pdf` or `image`): only a PDF page has a print size. */
    sourceKind?: string
    /** The trade the layer is filed under (spec 2026-10-21-11), null when unfiled. */
    discipline?: string | null
    /** The source document's date (`YYYY-MM-DD`), null when undated. */
    documentDate?: string | null
    /** Height of the crop in local units — its aspect ratio, width being 1. */
    height: number
    opacity: number
    visible: boolean
    /**
     * The underlay came from an ancestor's frame rather than the subject's own
     * (spec 2026-09-17-01). Unlike a context *element* it is drawn exactly as
     * any other underlay — it is the background base layer the view exists to
     * show — but the viewer reads the flag to know the raster is not the
     * subject's to delete.
     */
    context?: boolean
}

/** How a pending registration transform was obtained (spec §1, §2, §6). */
export interface RegistrationProvenance {
    method: 'manual' | 'anchor_pairs'
    confidence?: 'high' | 'medium' | 'low'
    pairs?: AnchorPair[]
}

/**
 * A registration whose transform the editor changed — by anchor-pair
 * calibration, by the measure tool, or by dragging the layer's bounding box.
 * `transform` is the **composed** source → edited-frame transform; the save
 * orchestrator converts it back to the registration's own hop.
 */
export interface RegistrationEdit {
    registrationUid: string
    /**
     * The composed transform the scene was loaded with. Kept across successive
     * edits so the registration's own hop can always be recovered
     * (`registrationTransformFor`), however many times the layer moved.
     */
    base: Transform
    transform: Transform
    provenance: RegistrationProvenance
}

export interface SceneDoc {
    frame: { uid: string; name: string; typeCode: string }
    /**
     * The asset the scene was requested for, when the server built it from an
     * ANCESTOR's frame because the asset's own was empty (spec 2026-09-17-01).
     * Absent means `frame` is also the subject, which is every editor document
     * and every frame-rooted read.
     */
    subjectAssetUid?: string
    elements: EditorElement[]
    underlays: UnderlayEntry[]
    symbols: Record<string, PlanSymbol>
    /**
     * The network catalogue runs are coloured and filed by, in ontology order.
     * Absent reads as empty: a run then draws neutral, under its raw code.
     */
    networks?: PlanNetwork[]
    /** Registration uid → the pending transform change, keyed for idempotence. */
    registrationEdits: Record<string, RegistrationEdit>
    /**
     * Representation uid → the value's `title`. Serialization rebuilds a value
     * from the elements alone, so the title has to be carried here or a PATCH
     * would silently drop it (an imported floorplan is named, spec §7).
     */
    repTitles: Record<string, string>
    /**
     * Representation uid → the value's `provenance`, for the bundles a
     * digitization produced (spec 2026-09-03-01 decision 4). Carried like the
     * title, and for the same reason: serialization rebuilds a value from the
     * elements alone and would otherwise drop it on the next save.
     */
    repProvenance?: Record<string, VectorProvenance>
    /**
     * The frame's resolved calibration (spec 2026-08-25-03 §3, §8) — how many
     * real millimetres one plan unit is worth. `null`/absent is a valid and
     * common state: the frame is simply **not to scale**, and every readout
     * says so instead of publishing nominal millimetres as measurements.
     */
    calibration?: FrameCalibration | null
    /**
     * The registration of the frame's **primary plan** — its drawing surface,
     * and the only row calibration is ever written to (spec 2026-08-25-04 §3).
     * Absent when the primary plan resolves implicitly (no row to PATCH) or
     * when the frame has no raster plan at all.
     */
    primaryRegistrationUid?: string
    /**
     * A pending calibration change, applied optimistically to `calibration` so
     * every readout updates live, and PATCHed on "apply". Never a transform:
     * calibrating moves nothing.
     */
    calibrationEdit?: { registrationUid: string; calibration: FrameCalibration | null; base: FrameCalibration | null } | null
    /**
     * Groupers created from the combobox rather than drawn: key → the asset the
     * save must materialize before it can write the `group_member` edges pointing at
     * it (spec 2026-08-30-01 §5). A *drawn* grouper needs no entry — it owns an
     * element, so its `owner.newAsset` is already in the diff.
     */
    pendingGroupers?: Record<string, PendingAsset>
}

let seq = 0
export function nextKey(prefix = 'el'): string {
    seq += 1
    return `${prefix}-${seq}-${Math.random().toString(36).slice(2, 7)}`
}

/** Fresh local id for an element inside a representation value. */
export function nextLocalId(prefix: string, taken: Set<string>): string {
    let n = 1
    let id = `${prefix}-${n}`
    while (taken.has(id)) {
        n += 1
        id = `${prefix}-${n}`
    }
    taken.add(id)
    return id
}

export function collectLocalIds(doc: SceneDoc): Set<string> {
    return new Set(doc.elements.map((e) => e.source.localId))
}

/**
 * The asset whose *representation* holds this element — the one a `wall_ref`
 * has to name. It differs from `owner.assetUid` exactly when a bundle depicts
 * an asset other than its own (spec §4).
 */
export const repAssetUidOf = (el: EditorElement): string | undefined =>
    el.source.repOwner?.assetUid ?? el.owner.assetUid

const transformOf = (t: Transform | undefined): Transform => ({
    tx: t?.tx ?? 0,
    ty: t?.ty ?? 0,
    rotation_deg: t?.rotation_deg ?? 0,
    scale: t?.scale && t.scale !== 0 ? t.scale : 1,
})

/* ------------------------------------------------------------------ loading */

const RASTER_KINDS = new Set(['pdf', 'image'])
/** A4 portrait, matching the backend's fallback when page dimensions are unknown. */
const FALLBACK_PAGE_W = 595
const FALLBACK_PAGE_H = 842

/**
 * A raster crop's height in its own local frame, whose width is 1 — i.e. its
 * aspect ratio.
 *
 * The server sends this on the scene entry because only it knows the source
 * page's real shape (`document_pages`). The A4-portrait arithmetic below is a
 * last-resort fallback for an entry that predates the field: guessing a page
 * shape squashes a landscape plan into a portrait box, so a served value always
 * wins.
 */
function underlayHeight(served: number | undefined, value: unknown): number {
    if (typeof served === 'number' && Number.isFinite(served) && served > 0) return served
    const sheets = (value as { sheets?: { bbox?: { w?: number; h?: number } }[] } | undefined)?.sheets
    const bbox = sheets?.[0]?.bbox
    if (!bbox || !bbox.w || !bbox.h || bbox.w <= 0 || bbox.h <= 0) return FALLBACK_PAGE_H / FALLBACK_PAGE_W
    return (bbox.h * FALLBACK_PAGE_H) / (bbox.w * FALLBACK_PAGE_W)
}

/** Builds the editable document from the scene endpoint's payload. */
export function sceneFromPlanScene(scene: PlanScene): SceneDoc {
    const doc: SceneDoc = {
        frame: {
            uid: scene.frame_asset?.uid ?? '',
            name: scene.frame_asset?.name ?? '',
            typeCode: scene.frame_asset?.type_code ?? '',
        },
        elements: [],
        underlays: [],
        symbols: scene.symbols ?? {},
        networks: sanitizeNetworks(scene.networks),
        registrationEdits: {},
        repTitles: {},
        repProvenance: {},
        calibration: scene.calibration ?? null,
        calibrationEdit: null,
        ...(scene.subject_asset?.uid ? { subjectAssetUid: scene.subject_asset.uid } : {}),
    }

    // The primary raster entry is the frame's drawing surface (spec 03 §4).
    // Only an entry with a registration row can carry a calibration — an
    // implicitly-resolved plan has nothing to PATCH.
    for (const entry of scene.entries ?? []) {
        if (!entry.primary || !entry.registration_uid) continue
        if (!RASTER_KINDS.has(entry.representation?.kind ?? '')) continue
        doc.primaryRegistrationUid = entry.registration_uid
        break
    }

    // (repUid, wallLocalId) → element key, so openings can be re-linked to
    // their host wall after every element exists.
    const wallIndex = new Map<string, string>()
    const pendingWallLinks: { opening: OpeningElement; repUid?: string; wallId?: string }[] = []

    for (const entry of scene.entries ?? []) {
        const rep = entry.representation
        if (!rep) continue
        const transform = transformOf(entry.transform)
        const repOwner: ElementOwner = {
            assetUid: rep.asset?.uid,
            assetName: rep.asset?.name,
            assetTypeCode: rep.asset?.type_code,
            assetParentUid: rep.asset?.parent_uid,
            // The naming state comes with the scene (spec 2026-08-31-02 §2):
            // it is the one payload that names every asset drawn here, where
            // the descendants list the shell also reads is paged and skips
            // archived assets. A room that list left out used to be edited
            // as if someone had typed its stored (English) name — clearing
            // it then "came back" as the same string, in black.
            ...(rep.asset?.auto_name ? { assetAutoName: true } : {}),
            ...(typeof rep.asset?.room_number === 'number' ? { assetRoomNumber: rep.asset.room_number } : {}),
        }
        // A bundle may depict assets other than its own: `properties.asset_uid`
        // is what a demoted element keeps to preserve the SVG id contract
        // (spec 2026-08-22-01 §1, spec 2026-08-22-03 §4). The name and type of
        // such an asset are not in the scene payload — `hydrateOwnerNames`
        // fills them in from the shell's asset index.
        const owner = (props?: Record<string, unknown>): ElementOwner => {
            const depicted = typeof props?.asset_uid === 'string' ? props.asset_uid : undefined
            if (depicted && depicted !== repOwner.assetUid) return { assetUid: depicted }
            return { ...repOwner }
        }
        const sourceOf = (localId: string): SourceRef => ({
            repUid: rep.uid,
            registrationUid: entry.registration_uid,
            transform,
            localId,
            repOwner: { ...repOwner },
            ...(entry.context ? { context: true } : {}),
        })

        if (RASTER_KINDS.has(rep.kind)) {
            doc.underlays.push({
                key: nextKey('underlay'),
                repUid: rep.uid,
                registrationUid: entry.registration_uid,
                // Deliberately NOT quieted the way a context element is: an
                // inherited scan *is* the background base layer the reader
                // asked for (spec 2026-09-17-01 §1).
                ...(entry.context ? { context: true } : {}),
                assetUid: rep.asset?.uid,
                assetName: rep.asset?.name,
                transform,
                sourceKind: rep.kind,
                discipline: rep.discipline ?? null,
                documentDate: rep.document_date ?? null,
                height: underlayHeight(entry.underlay_height, rep.value),
                opacity: 0.85,
                visible: true,
            })
            continue
        }
        if (rep.kind !== 'vector') continue

        const value = (rep.value ?? {}) as VectorValue
        if (value.title) doc.repTitles[rep.uid] = value.title
        if (value.provenance) (doc.repProvenance ??= {})[rep.uid] = value.provenance
        const localWalls = value.walls ?? []

        // No zoom is reachable while building the scene, so an uncalibrated
        // frame keeps the 100-unit default here (units read as millimetres).
        // Walls the digitizer or an import wrote always carry a thickness; only
        // hand-written JSON lacks one, and the renderer draws those on screen.
        for (const w of localWalls) {
            const el: WallElement = {
                key: nextKey('wall'),
                kind: 'wall',
                source: sourceOf(w.id),
                owner: owner(w.properties),
                start: applyTransform(transform, w.start),
                end: applyTransform(transform, w.end),
                thickness: (w.thickness && w.thickness > 0 ? w.thickness : mmToUnits(DEFAULT_WALL_THICKNESS, doc.calibration)) * transform.scale,
            }
            wallIndex.set(`${rep.uid}::${w.id}`, el.key)
            doc.elements.push(el)
        }

        for (const s of value.spaces ?? []) {
            const ring = dropClosingPoint(s.boundary ?? [])
            const holes = (s.holes ?? [])
                .map(dropClosingPoint)
                .filter((h) => h.length >= 3)
                .map((h) => h.map((p) => applyTransform(transform, p)))
            const typeHint = typeof s.properties?.type_code === 'string' ? s.properties.type_code : undefined
            const spaceTypeHint = typeof s.properties?.space_type === 'string' ? s.properties.space_type : undefined
            doc.elements.push({
                key: nextKey('space'),
                kind: 'space',
                source: sourceOf(s.id),
                owner: owner(s.properties),
                name: s.name || repOwner.assetName || '',
                boundary: ring.map((p) => applyTransform(transform, p)),
                ...(holes.length ? { holes } : {}),
                ...(typeHint ? { typeHint } : {}),
                ...(spaceTypeHint ? { spaceTypeHint } : {}),
            })
        }

        for (const o of value.openingEquipments ?? []) {
            const hostWall = o.wall_id ? localWalls.find((w) => w.id === o.wall_id) : undefined
            let center: Point
            let directionDeg: number
            let thickness = o.wall_thickness ?? mmToUnits(DEFAULT_WALL_THICKNESS, doc.calibration)
            if (hostWall) {
                const dx = hostWall.end.x - hostWall.start.x
                const dy = hostWall.end.y - hostWall.start.y
                const t = o.position ?? 0.5
                center = { x: hostWall.start.x + dx * t, y: hostWall.start.y + dy * t }
                directionDeg = (Math.atan2(dy, dx) * 180) / Math.PI
                thickness = hostWall.thickness && hostWall.thickness > 0 ? hostWall.thickness : thickness
            } else if (o.center) {
                center = o.center
                directionDeg = o.direction_deg ?? 0
            } else {
                continue
            }
            const openingKind: OpeningKind = isWindowKind(o.kind) ? 'window' : 'door'
            const el: OpeningElement = {
                key: nextKey('opening'),
                kind: 'opening',
                source: sourceOf(o.id),
                owner: owner(o.properties),
                openingKind,
                center: applyTransform(transform, center),
                directionDeg: directionDeg + transform.rotation_deg,
                width: (o.width && o.width > 0 ? o.width : mmToUnits(defaultWidth(openingKind), doc.calibration)) * transform.scale,
                wallThickness: thickness * transform.scale,
                operation: o.operation as Operation | undefined,
                swing: o.swing as Swing | undefined,
                leaves: o.leaves as 1 | 2 | undefined,
                rotation: o.rotation as 0 | 180 | undefined,
                wallRef: o.wall_ref ? { assetUid: o.wall_ref.asset_uid, wallId: o.wall_ref.wall_id } : undefined,
            }
            doc.elements.push(el)
            pendingWallLinks.push({ opening: el, repUid: rep.uid, wallId: o.wall_id })
        }

        for (const q of value.equipments ?? []) {
            const elementOwner = owner(q.properties)
            const kindHint = typeof q.properties?.kind_hint === 'string' ? q.properties.kind_hint : undefined
            const typeCode = (q.properties?.asset_type_code as string | undefined)
                ?? elementOwner.assetTypeCode
            const symbol = typeCode ? doc.symbols[typeCode] : undefined
            doc.elements.push({
                key: nextKey('equipment'),
                kind: 'equipment',
                source: sourceOf(q.id),
                owner: elementOwner,
                position: applyTransform(transform, q.position),
                rotationDeg: (q.rotation_deg ?? 0) + transform.rotation_deg,
                width: (q.dimensions?.width || symbol?.width_mm || 600) * transform.scale,
                depth: (q.dimensions?.depth || symbol?.depth_mm || 300) * transform.scale,
                typeCode,
                ...(kindHint ? { kindHint } : {}),
            })
        }

        for (const r of value.runs ?? []) {
            if ((r.points ?? []).length < 2) continue
            doc.elements.push({
                key: nextKey('run'),
                kind: 'run',
                source: sourceOf(r.id),
                owner: owner(r.properties),
                network: r.network,
                points: r.points.map((p) => applyTransform(transform, p)),
                ...(r.width_mm && r.width_mm > 0 ? { widthMm: r.width_mm } : {}),
            })
        }

        for (const r of value.risers ?? []) {
            const label = typeof r.properties?.label === 'string' ? r.properties.label : undefined
            doc.elements.push({
                key: nextKey('riser'),
                kind: 'riser',
                source: sourceOf(r.id),
                owner: owner(r.properties),
                network: r.network,
                position: applyTransform(transform, r.at),
                ...(r.diameter_mm && r.diameter_mm > 0 ? { diameterMm: r.diameter_mm } : {}),
                ...(r.passage ? { passage: r.passage } : {}),
                ...(label ? { label } : {}),
            })
        }
    }

    // Re-link openings onto their host wall element: first the in-value
    // `wall_id`, then the cross-representation `wall_ref`. A ref that resolves
    // to nothing stays dangling — a validation warning, never a load failure.
    const byAssetWall = new Map<string, string>()
    for (const el of doc.elements) {
        const host = repAssetUidOf(el)
        if (el.kind === 'wall' && host) byAssetWall.set(`${host}::${el.source.localId}`, el.key)
    }
    for (const link of pendingWallLinks) {
        if (link.wallId) {
            const key = wallIndex.get(`${link.repUid}::${link.wallId}`)
            if (key) {
                link.opening.wallKey = key
                continue
            }
        }
        const ref = link.opening.wallRef
        if (ref) {
            const key = byAssetWall.get(`${ref.assetUid}::${ref.wallId}`)
            if (key) link.opening.wallKey = key
        }
    }

    return doc
}

/** floorplan-v1 accepts a repeated first point; the editor keeps open rings. */
function dropClosingPoint(ring: Point[]): Point[] {
    if (ring.length >= 4) {
        const a = ring[0]
        const b = ring[ring.length - 1]
        if (Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6) return ring.slice(0, -1)
    }
    return ring
}

export const cloneDoc = (doc: SceneDoc): SceneDoc => JSON.parse(JSON.stringify(doc)) as SceneDoc

/* ------------------------------------------------------------ serialization */

/** The identity of the representation an element belongs to, for grouping. */
export function groupIdOf(el: EditorElement): string {
    if (el.source.repUid) return `rep:${el.source.repUid}`
    // A new run joins the bundle it was drawn into, whatever network asset it
    // depicts — see RunElement.
    if ((el.kind === 'run' || el.kind === 'riser') && el.source.repOwner?.assetUid) return `newrep:asset:${el.source.repOwner.assetUid}`
    if (el.owner.assetUid) return `newrep:asset:${el.owner.assetUid}`
    if (el.owner.newAsset) return `newrep:pending:${el.owner.newAsset.key}`
    return `newrep:orphan:${el.key}`
}

export interface RepGroup {
    id: string
    repUid?: string
    transform: Transform
    owner: ElementOwner
    elements: EditorElement[]
}

export function groupByRepresentation(doc: SceneDoc): RepGroup[] {
    const groups = new Map<string, RepGroup>()
    for (const el of doc.elements) {
        const id = groupIdOf(el)
        let g = groups.get(id)
        if (!g) {
            g = {
                id,
                repUid: el.source.repUid,
                transform: el.source.repUid ? el.source.transform : IDENTITY,
                // A saved representation belongs to its own asset even when it
                // depicts others (a bundle); only new geometry takes the
                // element's owner, because that is what will be created.
                owner: el.source.repUid || el.kind === 'run' || el.kind === 'riser' ? el.source.repOwner ?? el.owner : el.owner,
                elements: [],
            }
            groups.set(id, g)
        }
        g.elements.push(el)
    }
    return [...groups.values()]
}

const round = (v: number): number => Math.round(v * 1000) / 1000
const roundPoint = (p: Point): Point => ({ x: round(p.x), y: round(p.y) })

/**
 * Serializes one representation group back into a `VectorRepresentationValue`,
 * inverting the group's transform so the geometry lands in the representation's
 * own local frame.
 */
export function serializeGroup(group: RepGroup, doc: SceneDoc): VectorValue {
    const inv = invertTransform(group.transform)
    const invScale = inv.scale
    const title = group.repUid ? doc.repTitles?.[group.repUid] : undefined
    // A digitized bundle keeps saying where it came from across every edit:
    // serialization rebuilds the value from the elements, so anything not
    // carried here is silently dropped by the next save (spec 2026-09-03-01).
    const provenance = group.repUid ? doc.repProvenance?.[group.repUid] : undefined
    const value: VectorValue = {
        version: 'floorplan-v1',
        units: 'mm',
        ...(title ? { title } : {}),
        ...(provenance ? { provenance } : {}),
    }
    const walls: NonNullable<VectorValue['walls']> = []
    const spaces: NonNullable<VectorValue['spaces']> = []
    const openings: NonNullable<VectorValue['openingEquipments']> = []
    const equipments: NonNullable<VectorValue['equipments']> = []
    const runs: NonNullable<VectorValue['runs']> = []
    const risers: NonNullable<VectorValue['risers']> = []
    const localWallKeys = new Set(group.elements.filter((e) => e.kind === 'wall').map((e) => e.key))
    const byKey = new Map(doc.elements.map((e) => [e.key, e]))

    for (const el of group.elements) {
        switch (el.kind) {
            case 'wall':
                walls.push({
                    id: el.source.localId,
                    start: roundPoint(applyTransform(inv, el.start)),
                    end: roundPoint(applyTransform(inv, el.end)),
                    thickness: round(el.thickness * invScale),
                    ...(el.owner.assetUid ? { properties: { asset_uid: el.owner.assetUid } } : {}),
                })
                break
            case 'space':
                spaces.push({
                    id: el.source.localId,
                    // The drawing's own label only — never a copy of the asset's
                    // name. An auto-named room's "Chambre 2" is stored on the
                    // asset (spec 2026-08-31-02 §2) and every renderer falls
                    // back to the asset name when this is empty, so copying it
                    // here would only leave a stale label behind the next
                    // renumbering.
                    name: el.name,
                    boundary: el.boundary.map((p) => roundPoint(applyTransform(inv, p))),
                    ...(el.holes?.length
                        ? { holes: el.holes.map((h) => h.map((p) => roundPoint(applyTransform(inv, p)))) }
                        : {}),
                    ...(el.owner.assetUid || el.typeHint || el.spaceTypeHint
                        ? {
                            properties: {
                                ...(el.owner.assetUid ? { asset_uid: el.owner.assetUid } : {}),
                                // The digitization's proposed room type — and the
                                // model's own word for it — survive every edit
                                // until promotion consumes them.
                                ...(el.typeHint ? { type_code: el.typeHint } : {}),
                                ...(el.spaceTypeHint ? { space_type: el.spaceTypeHint } : {}),
                            },
                        }
                        : {}),
                })
                break
            case 'opening': {
                const host = el.wallKey ? byKey.get(el.wallKey) : undefined
                const out: NonNullable<VectorValue['openingEquipments']>[number] = {
                    id: el.source.localId,
                    kind: el.openingKind,
                    center: roundPoint(applyTransform(inv, el.center)),
                    direction_deg: round(el.directionDeg + inv.rotation_deg),
                    width: round(el.width * invScale),
                    wall_thickness: round(el.wallThickness * invScale),
                    ...(el.operation ? { operation: el.operation } : {}),
                    ...(el.swing ? { swing: el.swing } : {}),
                    ...(el.leaves === 2 ? { leaves: 2 as const } : {}),
                    ...(el.rotation === 180 ? { rotation: 180 as const } : {}),
                    ...(el.owner.assetUid ? { properties: { asset_uid: el.owner.assetUid } } : {}),
                }
                if (host && host.kind === 'wall') {
                    if (localWallKeys.has(host.key)) {
                        // Same representation: a real local attachment.
                        const t = projectToLine(el.center, host.start, host.end).t
                        if (t >= 0 && t <= 1) {
                            out.wall_id = host.source.localId
                            out.position = round(t)
                        }
                    } else {
                        const hostAsset = repAssetUidOf(host)
                        if (hostAsset) out.wall_ref = { asset_uid: hostAsset, wall_id: host.source.localId }
                    }
                } else if (el.wallRef) {
                    out.wall_ref = { asset_uid: el.wallRef.assetUid, wall_id: el.wallRef.wallId }
                }
                openings.push(out)
                break
            }
            case 'equipment':
                equipments.push({
                    id: el.source.localId,
                    kind: el.typeCode,
                    position: roundPoint(applyTransform(inv, el.position)),
                    rotation_deg: round(el.rotationDeg + inv.rotation_deg),
                    dimensions: { width: round(el.width * invScale), depth: round(el.depth * invScale) },
                    properties: {
                        ...(el.owner.assetUid ? { asset_uid: el.owner.assetUid } : {}),
                        ...(el.typeCode ? { asset_type_code: el.typeCode } : {}),
                        ...(el.kindHint ? { kind_hint: el.kindHint } : {}),
                    },
                })
                break
            case 'run':
                runs.push({
                    id: el.source.localId,
                    network: el.network,
                    points: el.points.map((p) => roundPoint(applyTransform(inv, p))),
                    // Millimetres, not plan units: it does not go through the
                    // inverse scale.
                    ...(el.widthMm && el.widthMm > 0 ? { width_mm: round(el.widthMm) } : {}),
                    ...(el.owner.assetUid
                        ? { properties: { asset_uid: el.owner.assetUid } }
                        : el.owner.newAsset
                            ? { properties: { asset_uid: `${PENDING_LINK_PREFIX}${el.owner.newAsset.key}` } }
                            : {}),
                })
                break
            case 'riser': {
                // The column is a link: the bundle's own owner is no column.
                const column = el.owner.assetUid && el.owner.assetUid !== el.source.repOwner?.assetUid
                    ? el.owner.assetUid : undefined
                const properties = {
                    ...(column ? { asset_uid: column } : {}),
                    ...(el.label ? { label: el.label } : {}),
                }
                risers.push({
                    id: el.source.localId,
                    network: el.network,
                    at: roundPoint(applyTransform(inv, el.position)),
                    // Millimetres, like a run's width.
                    ...(el.diameterMm && el.diameterMm > 0 ? { diameter_mm: round(el.diameterMm) } : {}),
                    ...(el.passage ? { passage: el.passage } : {}),
                    ...(Object.keys(properties).length ? { properties } : {}),
                })
                break
            }
        }
    }

    if (walls.length) value.walls = walls
    if (spaces.length) value.spaces = spaces
    if (openings.length) value.openingEquipments = openings
    if (equipments.length) value.equipments = equipments
    if (runs.length) value.runs = runs
    if (risers.length) value.risers = risers
    return value
}

/* -------------------------------------------------------------------- diff */

export interface AssetCreationOp {
    /** Matches `PendingAsset.key` — the link between an asset op and its rep op. */
    key: string
    name: string
    typeCode: string
    /** The grouper's public code, written as an external id (§3). */
    code?: string
    /** The grouper's `unit_kind`, written as a property value (§1). */
    unitKind?: string
    /** The room's number, written as a property value (spec 2026-08-31-02 §1). */
    roomNumber?: number
    /** A network asset's `network_kind`, written as a property value. */
    networkKind?: string
    parentUid?: string
    /**
     * The pending asset this one parents to (spec 2026-08-25-04 §2). Ops are
     * emitted parents-first, so the orchestrator always has the uid by the time
     * it reaches the child.
     */
    parentKey?: string
}

export interface RepCreationOp {
    groupId: string
    /** The owning asset when it already exists. */
    assetUid?: string
    /** The pending asset's key when the asset is created by the same save. */
    assetKey?: string
    value: VectorValue
    label: string
    /**
     * Write an explicit registration into the edited frame (spec 2026-08-25-04
     * §1). False for the overwhelming majority: geometry authored in its own
     * frame resolves implicitly and a row would be pure ceremony. True only for
     * a **cross-layer footprint** — an asset whose implicit frame is elsewhere
     * (the building's elevator drawn on floor 3), where the row is what says
     * *which layer*.
     */
    registerInFrame?: boolean
}

export interface RepUpdateOp {
    repUid: string
    value: VectorValue
    label: string
}

export interface RepDeleteOp {
    repUid: string
    assetUid?: string
    assetName?: string
    label: string
}

/**
 * An existing asset the user re-parented from the inspector (§2 override, §6
 * guard). Its implicit frame follows its parent, which is why the UI confirms
 * before queueing one.
 */
export interface AssetReparentOp {
    assetUid: string
    parentUid: string
    label: string
}

/**
 * A field edit on an asset that already exists (spec 2026-08-30-03 §4). Every
 * field is optional and only set when it actually changed, so the save writes
 * the narrowest possible request — and `unitKind` travels separately from the
 * rest because it is a property value, not a column.
 */
export interface AssetUpdateOp {
    assetUid: string
    label: string
    name?: string
    typeCode?: string
    /** `''` clears the code — an empty external id is no external id. */
    code?: string
    unitKind?: string
    /** A room's number, written as a property value like `unitKind`. */
    roomNumber?: number
}

/**
 * A space's membership edge to write (spec 2026-08-30-01 §9). "Create" covers
 * replacement too: `replacesRelationUid` is the row the save deletes first, so
 * a space never ends up a `group_member` of two groupers.
 */
export interface RelationCreateOp {
    /** The space's asset, when it already exists. */
    sourceUid?: string
    /** The pending space asset's key, when the same save creates it. */
    sourceKey?: string
    targetUid?: string
    targetKey?: string
    replacesRelationUid?: string
    label: string
    targetLabel: string
}

/** A membership edge the user cleared. */
export interface RelationDeleteOp {
    relationUid: string
    label: string
}

export interface PlanDiff {
    createAssets: AssetCreationOp[]
    /**
     * Pending assets the ontology has nowhere legal to file (spec
     * 2026-09-18-01): no ancestor of the point they were drawn at has a type
     * they may hang from, so the API would answer 422.
     *
     * They are reported INSTEAD of being emitted as creation ops — a save that
     * posts them loses the drawing to a refusal the user never sees. The page
     * refuses the whole save while this is non-empty and names them.
     */
    refusedAssets: { key: string; name: string; typeCode: string }[]
    createReps: RepCreationOp[]
    updateReps: RepUpdateOp[]
    deleteReps: RepDeleteOp[]
    /**
     * Registrations whose transform changed — anchor-pair calibration, the
     * measure tool, or a layer drag (spec 2026-08-22-03 §1, §2, §6). The
     * transform carried here is the composed one; the save orchestrator
     * converts it to the registration's own hop before patching.
     */
    updateRegistrations: RegistrationEdit[]
    /**
     * Assets whose geometry was removed. Deleting an element never deletes its
     * asset — it deletes the representation and reports the asset here so the
     * post-save summary can say "N assets are no longer drawn" (§4).
     */
    orphanedAssets: { uid: string; name: string }[]
    /** Existing assets whose parent the inspector changed (§2 / §6). */
    reparentAssets: AssetReparentOp[]
    /**
     * Existing assets whose name, kind or code the inspector changed
     * (spec 2026-08-30-03 §4). Separate from `reparentAssets` because the two
     * answer different questions and one can fail without the other: a rename
     * refused for a duplicate must not look like a failed move.
     */
    updateAssets: AssetUpdateOp[]
    /** `group_member` edges to write — the grouping combobox (spec 2026-08-30-01 §9). */
    createRelations: RelationCreateOp[]
    /** `group_member` edges to remove, from a cleared or replaced grouping. */
    deleteRelations: RelationDeleteOp[]
    /**
     * The frame's calibration, when the user changed it. A `calibration` PATCH
     * on the primary plan's registration — never a transform (§3).
     */
    updateCalibration?: { registrationUid: string; calibration: FrameCalibration | null }
}

export const isEmptyDiff = (d: PlanDiff): boolean =>
    !d.createAssets.length && !d.createReps.length && !d.updateReps.length && !d.deleteReps.length &&
    !d.updateRegistrations.length && !d.reparentAssets.length && !d.updateCalibration &&
    !d.createRelations.length && !d.deleteRelations.length && !d.updateAssets.length

// A room still pending — or auto-named and being retyped — has to be *called*
// something in a save summary or a failure line, and that is what its live
// derivation is for (spec 2026-08-31-02 §2). The stored name and then the uid
// are the last resorts they always were.
const labelOf = (owner: ElementOwner, typeLabelOf?: (code: string) => string): string =>
    ownerName(owner)
    || (typeLabelOf ? spaceFallbackName(owner, typeLabelOf) : '')
    || owner.assetName || owner.assetUid || '—'

export interface DiffOptions {
    /**
     * Assets whose footprint survives on **another layer** (spec 2026-08-25-04
     * §5). Deleting this layer's drawing of a shared asset is not "no longer
     * drawn anywhere", so those never reach `orphanedAssets`.
     */
    drawnElsewhere?: Set<string>
    /**
     * Each existing grouper's **stored** code and `unit_kind`, straight from
     * the asset lists (spec 2026-08-30-03 §4). The doc carries only what the
     * inspector *changed*, so this is what "changed" is measured against —
     * deliberately not stamped onto the document, because the lists settle on
     * their own schedule and a doc seeded before they land would read every
     * blank as an edit.
     */
    grouperMeta?: Map<string, { code?: string; unitKind?: string }>
    /**
     * Localizes an asset type code, so a pending or auto-named room is called
     * "Chambre 2" in the save summary (spec 2026-08-31-02 §2). Optional: labels
     * only, never what a save writes.
     */
    typeLabelOf?: (code: string) => string
}

/** Computes the ordered API work that turns `original` into `current`. */
export function computeDiff(original: SceneDoc, current: SceneDoc, opts: DiffOptions = {}): PlanDiff {
    const diff: PlanDiff = {
        createAssets: [],
        refusedAssets: [],
        createReps: [],
        updateReps: [],
        deleteReps: [],
        updateRegistrations: [],
        orphanedAssets: [],
        reparentAssets: [],
        updateAssets: [],
        createRelations: [],
        deleteRelations: [],
    }

    for (const edit of Object.values(current.registrationEdits ?? {})) {
        const before = original.registrationEdits?.[edit.registrationUid]
        if (before && JSON.stringify(before) === JSON.stringify(edit)) continue
        diff.updateRegistrations.push(edit)
    }

    const calibrationEdit = current.calibrationEdit
    if (calibrationEdit && JSON.stringify(calibrationEdit.calibration) !== JSON.stringify(calibrationEdit.base)) {
        diff.updateCalibration = {
            registrationUid: calibrationEdit.registrationUid,
            calibration: calibrationEdit.calibration,
        }
    }

    // Pending assets, **parents before children**: a radiator dropped in a room
    // drawn seconds ago parents to that room, whose uid only exists once its
    // own create has returned (§2).
    const pendingByKey = new Map<string, PendingAsset>()
    for (const el of current.elements) {
        const pending = el.owner.newAsset
        if (pending && !pendingByKey.has(pending.key)) pendingByKey.set(pending.key, pending)
    }
    // Groupers typed into the combobox own no geometry, so nothing above would
    // ever emit them. Only those something actually points at are created: a
    // grouper the user conjured and then changed their mind about must not
    // leave an empty flat behind.
    const referencedGrouperKeys = new Set(
        current.elements.map((el) => el.owner.grouper?.assetKey).filter((k): k is string => Boolean(k)),
    )
    for (const key of referencedGrouperKeys) {
        const pending = current.pendingGroupers?.[key]
        if (pending && !pendingByKey.has(pending.key)) pendingByKey.set(pending.key, pending)
    }
    const emitted = new Set<string>()
    const emit = (pending: PendingAsset, guard: Set<string>): void => {
        if (emitted.has(pending.key) || guard.has(pending.key)) return
        guard.add(pending.key)
        const parent = pending.parentKey ? pendingByKey.get(pending.parentKey) : undefined
        if (parent) emit(parent, guard)
        emitted.add(pending.key)
        if (pending.parentRefused) {
            // No creation op at all: the API refuses this parent, and a POST
            // that 422s would take the element's geometry down with it.
            diff.refusedAssets.push({ key: pending.key, name: pending.name, typeCode: pending.typeCode })

            return
        }
        diff.createAssets.push({
            key: pending.key,
            name: pending.name,
            typeCode: pending.typeCode,
            ...(pending.code ? { code: pending.code } : {}),
            ...(pending.unitKind ? { unitKind: pending.unitKind } : {}),
            ...(pending.roomNumber !== undefined ? { roomNumber: pending.roomNumber } : {}),
            ...(pending.networkKind ? { networkKind: pending.networkKind } : {}),
            parentUid: pending.parentUid,
            parentKey: pending.parentKey,
        })
    }
    for (const pending of pendingByKey.values()) emit(pending, new Set())

    // Existing assets the inspector re-parented (§2 override, guarded by §6).
    const reparented = new Map<string, AssetReparentOp>()
    const originalParents = new Map<string, string | undefined>()
    for (const el of original.elements) {
        if (el.owner.assetUid) originalParents.set(el.owner.assetUid, el.owner.assetParentUid)
    }
    for (const el of current.elements) {
        const uid = el.owner.assetUid
        // Only an override the inspector wrote counts. Reading `assetParentUid`
        // straight made every *late-hydrated* parent look like a re-parent, and
        // an untouched plan saved one PATCH per depicted asset.
        const edited = el.owner.assetParentEdit
        if (!uid || !edited) continue
        if (!originalParents.has(uid)) continue
        if (originalParents.get(uid) === edited) continue
        reparented.set(uid, {
            assetUid: uid,
            parentUid: edited,
            label: el.owner.assetName || uid,
        })
    }
    diff.reparentAssets = [...reparented.values()]

    // Name / kind / code edits on assets that already exist (§4). Keyed by
    // asset so a space depicted on two layers still produces one PATCH, and
    // compared against the *original doc* — the loader stamps the stored code
    // and `unit_kind` onto both, so an untouched grouper diffs to nothing.
    const originalOwners = new Map<string, ElementOwner>()
    for (const el of original.elements) {
        if (el.owner.assetUid && !originalOwners.has(el.owner.assetUid)) {
            originalOwners.set(el.owner.assetUid, el.owner)
        }
    }
    const updated = new Map<string, AssetUpdateOp>()
    const seenAssets = new Set<string>()
    for (const el of current.elements) {
        const uid = el.owner.assetUid
        if (!uid || seenAssets.has(uid)) continue
        seenAssets.add(uid)
        const before = originalOwners.get(uid)
        if (!before) continue
        // A rename is the one edit every owned element can carry: the panel's
        // Name field edits the **asset**, whatever kind of thing it depicts
        // (spec 2026-08-30-03 §12).
        if (el.owner.assetNameEdit !== undefined && el.owner.assetNameEdit !== before.assetName) {
            updated.set(uid, {
                assetUid: uid,
                label: el.owner.assetNameEdit || uid,
                name: el.owner.assetNameEdit,
            })
        }
        // A room's sub-type, picked in the panel (spec 2026-08-31-01 §6). Read
        // off `assetTypeEdit` rather than off `assetTypeCode` for the reason
        // spelled out just below, and compared against the type the asset
        // actually had — so picking the value it already carries is not an edit.
        if (el.owner.assetTypeEdit !== undefined && el.owner.assetTypeEdit !== before.assetTypeCode) {
            updated.set(uid, {
                ...updated.get(uid),
                assetUid: uid,
                label: el.owner.assetNameEdit || el.owner.assetName || uid,
                typeCode: el.owner.assetTypeEdit,
            })
        }
        // …and its number, beside it (spec 2026-08-31-02 §6). Compared against
        // the stored value, which the loader stamps onto both docs.
        if (el.owner.assetRoomNumberEdit !== undefined
            && el.owner.assetRoomNumberEdit !== before.assetRoomNumber) {
            updated.set(uid, {
                ...updated.get(uid),
                assetUid: uid,
                label: el.owner.assetNameEdit || el.owner.assetName || uid,
                roomNumber: el.owner.assetRoomNumberEdit,
            })
        }
        // Kind and code are groupers-only — and so is the type comparison, for
        // a reason that is not fussiness: an owner's `assetTypeCode` moves for
        // reasons that are not edits at all, since promoting an element out of
        // a bundle and demoting it back rewrites owners without touching a
        // single asset.
        if (!isGrouperElement(el)) continue
        const stored = opts.grouperMeta?.get(uid)
        const op: AssetUpdateOp = { assetUid: uid, label: el.owner.assetName || uid }
        if (el.owner.assetTypeCode !== before.assetTypeCode) op.typeCode = el.owner.assetTypeCode
        // `assetCode` / `assetUnitKind` are set by the inspector and by nothing
        // else, so an undefined one is "untouched", not "cleared".
        if (el.owner.assetCode !== undefined && el.owner.assetCode !== (stored?.code ?? '')) {
            op.code = el.owner.assetCode
        }
        if (el.owner.assetUnitKind !== undefined && el.owner.assetUnitKind !== (stored?.unitKind ?? '')) {
            op.unitKind = el.owner.assetUnitKind
        }
        if (op.typeCode === undefined && op.code === undefined && op.unitKind === undefined) continue
        // Fold into the rename recorded above rather than replacing it.
        updated.set(uid, { ...updated.get(uid), ...op })
    }
    diff.updateAssets = [...updated.values()]

    // Membership edges. Keyed by the *space asset* rather than by element so a
    // space depicted twice on one plan still writes one edge.
    const originalGrouperByKey = new Map<string, GrouperRef | undefined>()
    for (const el of original.elements) originalGrouperByKey.set(el.key, el.owner.grouper)
    const targetOf = (g?: GrouperRef): string | undefined => g?.assetUid ?? g?.assetKey
    const seenSpaces = new Set<string>()
    for (const el of current.elements) {
        if (el.kind !== 'space') continue
        const identity = el.owner.assetUid ?? el.owner.newAsset?.key ?? el.key
        if (seenSpaces.has(identity)) continue
        seenSpaces.add(identity)
        const before = originalGrouperByKey.get(el.key)
        const now = el.owner.grouper
        if (targetOf(before) === targetOf(now)) continue
        const label = labelOf(el.owner, opts.typeLabelOf)
        if (!targetOf(now)) {
            if (before?.relationUid) diff.deleteRelations.push({ relationUid: before.relationUid, label })
            continue
        }
        diff.createRelations.push({
            sourceUid: el.owner.assetUid,
            sourceKey: el.owner.newAsset?.key,
            targetUid: now?.assetUid,
            targetKey: now?.assetKey,
            ...(before?.relationUid ? { replacesRelationUid: before.relationUid } : {}),
            label,
            targetLabel: now?.name ?? now?.assetUid ?? '—',
        })
    }

    const currentGroups = groupByRepresentation(current)
    const originalGroups = new Map(
        groupByRepresentation(original)
            .filter((g) => g.repUid)
            .map((g) => [g.repUid as string, g]),
    )

    const survivingRepUids = new Set<string>()
    for (const g of currentGroups) {
        const value = serializeGroup(g, current)
        if (g.repUid) {
            survivingRepUids.add(g.repUid)
            const before = originalGroups.get(g.repUid)
            const beforeValue = before ? serializeGroup(before, original) : undefined
            if (!beforeValue || JSON.stringify(beforeValue) !== JSON.stringify(value)) {
                diff.updateReps.push({ repUid: g.repUid, value, label: labelOf(g.owner, opts.typeLabelOf) })
            }
            continue
        }
        diff.createReps.push({
            groupId: g.id,
            assetUid: g.owner.assetUid,
            assetKey: g.owner.newAsset?.key,
            value,
            label: labelOf(g.owner, opts.typeLabelOf),
            // The *only* registration a geometry save still writes (§1).
            ...(g.owner.crossLayer ? { registerInFrame: true } : {}),
        })
    }

    for (const [repUid, g] of originalGroups) {
        if (survivingRepUids.has(repUid)) continue
        diff.deleteReps.push({
            repUid,
            assetUid: g.owner.assetUid,
            assetName: g.owner.assetName,
            label: labelOf(g.owner, opts.typeLabelOf),
        })
        if (g.owner.assetUid) {
            const stillDrawn = current.elements.some((el) => el.owner.assetUid === g.owner.assetUid)
                || (opts.drawnElsewhere?.has(g.owner.assetUid) ?? false)
            if (!stillDrawn) {
                diff.orphanedAssets.push({ uid: g.owner.assetUid, name: g.owner.assetName ?? g.owner.assetUid })
            }
        }
    }

    return diff
}

/* ------------------------------------------------------------------ helpers */

/**
 * The **deepest** space containing a point (spec 2026-08-25-04 §2: "auto-parent
 * to the deepest containing drawn asset").
 *
 * Spaces nest — a closet drawn inside a bedroom, a shower room inside a
 * bathroom — and the polygons carry no hierarchy, so "deepest" is read off the
 * geometry: of every ring that contains the point, the one with the smallest
 * area is the innermost. Returning the *first* match instead would parent a
 * radiator in a closet to whichever of the two rooms the payload happened to
 * list first.
 */
export function containingSpace(doc: SceneDoc, p: Point): SpaceElement | undefined {
    let best: SpaceElement | undefined
    let bestArea = Number.POSITIVE_INFINITY
    for (const el of doc.elements) {
        if (el.kind !== 'space' || el.boundary.length < 3) continue
        if (!pointInSpace(p, el)) continue
        const area = shoelace(el.boundary)
        if (area < bestArea) {
            best = el
            bestArea = area
        }
    }
    return best
}

/** The two asset types that group spaces (spec 2026-08-30-01 §2). */
export const GROUPER_TYPE_CODES = ['unit', 'common_area'] as const
export type GrouperTypeCode = (typeof GROUPER_TYPE_CODES)[number]

/**
 * Does this type group spaces? `unit`, any lot subtype (`unit_housing`...,
 * spec 2026-10-09-04, read through the `unit_` prefix contract) and
 * `common_area`.
 */
export const isGrouperTypeCode = (code: string | undefined): boolean =>
    code !== undefined && (GROUPER_TYPE_CODES as readonly string[]).includes(lotBaseTypeCode(code))

/** The asset type an element depicts — pending type first, stored type second. */
export const elementTypeCode = (el: EditorElement): string | undefined =>
    el.owner.newAsset?.typeCode ?? el.owner.assetTypeCode

/** The property code carrying a unit's physical nature. */
export const UNIT_KIND_PROPERTY = 'unit_kind'

/** The property code carrying a room's number — the 2 of "Chambre 2". */
export const ROOM_NUMBER_PROPERTY = 'room_number'

/** The property code carrying the network of a network asset. */
export const NETWORK_KIND_PROPERTY = 'network_kind'

/**
 * The asset flag saying the stored name is the server's own derivation from
 * the type and the number (spec 2026-08-31-02 §2).
 */
export const AUTO_NAME_FLAG = 'auto_name'

/**
 * What a drawn grouper **is**, as one flat list (spec 2026-08-30-03 §1).
 *
 * Housing, parking and basement are the lot subtypes `unit_housing`,
 * `unit_parking` and `unit_cellar` (spec 2026-10-09-04), so only housing may
 * hold the rooms drawn inside it. The kind still writes the matching
 * `unit_kind`, which the ontology derives from the type for one release.
 *
 * `space_parking` is deliberately *not* the parking entry: that type is the
 * car park as a facility (it counts spaces and levels), not one numbered lot
 * sold with a flat.
 */
export const GROUPER_KINDS = [
    { id: 'housing', typeCode: 'unit_housing', unitKind: 'apartment' },
    { id: 'common', typeCode: 'common_area' },
    { id: 'parking', typeCode: 'unit_parking', unitKind: 'parking' },
    { id: 'basement', typeCode: 'unit_cellar', unitKind: 'cellar' },
] as const

export type GrouperKindId = (typeof GROUPER_KINDS)[number]['id']

/**
 * The kind an existing grouper reads as. A `unit` whose `unit_kind` is one the
 * editor does not offer — `commercial`, `display_panel` — still has to show
 * *something*, and housing is the least surprising default for a lot; changing
 * the select is what would overwrite the real value, and that is an explicit
 * gesture.
 */
export function grouperKindOf(typeCode?: string, unitKind?: string): GrouperKindId {
    if (typeCode === 'common_area') return 'common'
    // A lot subtype says what it is; a plain `unit` (created before the
    // subtypes, or by an import that could not tell) is read off its unit_kind.
    const byType = GROUPER_KINDS.find((k) => k.typeCode === typeCode)
    if (byType) return byType.id
    const byKind = GROUPER_KINDS.find((k) => 'unitKind' in k && k.unitKind === unitKind)
    return byKind?.id ?? 'housing'
}

/** The type / `unit_kind` pair a kind writes. */
export const grouperKindSpec = (id: GrouperKindId): { typeCode: string; unitKind?: string } => {
    const found: { id: string; typeCode: string; unitKind?: string } =
        GROUPER_KINDS.find((k) => k.id === id) ?? GROUPER_KINDS[0]
    return { typeCode: found.typeCode, ...(found.unitKind ? { unitKind: found.unitKind } : {}) }
}

/**
 * What a room nobody named reads as: `{type label} {number}` — "Chambre 2" —
 * or just the type label when it has no number (spec 2026-08-31-02 §2).
 *
 * The server stores exactly this on the asset and owns it through the
 * `auto_name` flag. The editor derives it too, from the type and number *as
 * currently edited*, so the plan reads right before the save that will make
 * the server re-derive its own — in the organization's language, where the
 * editor uses the reader's.
 */
export const roomFallbackName = (typeLabel: string, roomNumber?: number): string =>
    roomNumber === undefined || Number.isNaN(roomNumber) ? typeLabel : `${typeLabel} ${roomNumber}`

/** The type an owner reads as, pending or saved, panel edit included. */
export const ownerTypeCode = (owner: ElementOwner): string | undefined =>
    owner.newAsset ? owner.newAsset.typeCode : owner.assetTypeEdit ?? owner.assetTypeCode

/** The room number an owner reads as, pending or saved, panel edit included. */
export const ownerRoomNumber = (owner: ElementOwner): number | undefined =>
    owner.newAsset ? owner.newAsset.roomNumber : owner.assetRoomNumberEdit ?? owner.assetRoomNumber

/**
 * The name an owner reads as — `undefined` for a room nobody named. An
 * auto-named room counts as one: its stored name is the server's derivation,
 * which the editor recomputes live instead of reading back stale.
 */
export const ownerName = (owner: ElementOwner): string | undefined => {
    if (owner.newAsset) return owner.newAsset.name || undefined
    if (owner.assetNameEdit !== undefined) return owner.assetNameEdit || undefined
    return (owner.assetAutoName ? undefined : owner.assetName) || undefined
}

/**
 * Is this owner's name the **system's** to write rather than a person's?
 *
 * True for a room nobody has named: the asset carries the server's `auto_name`
 * flag, or the panel has just cleared the name and the save is about to hand it
 * back (a pending asset drawn without one counts too). What it buys is
 * precedence over the *drawing's* own label: a representation keeps whatever
 * label it was imported or saved with, so a room renamed back to the system
 * kept printing the very name that was just deleted while the inspector showed
 * the derivation two panels away. When nobody owns the name, the derivation
 * wins over that leftover; when someone types one, it wins over everything.
 */
export const ownerNameIsDerived = (owner: ElementOwner): boolean => {
    if (owner.newAsset) return !owner.newAsset.name
    if (owner.assetNameEdit !== undefined) return owner.assetNameEdit === ''
    return Boolean(owner.assetAutoName)
}

/**
 * What a space with no asset name is called: `{type label} {number}`.
 *
 * `typeLabelOf` localizes an asset type code — the ontology's own name for the
 * type, so "Chambre 2" in French and "Bedroom 2" in English fall out of the
 * catalog rather than out of a translation key here.
 */
export function spaceFallbackName(owner: ElementOwner, typeLabelOf: (code: string) => string): string {
    const code = ownerTypeCode(owner)
    return code ? roomFallbackName(typeLabelOf(code), ownerRoomNumber(owner)) : ''
}

/** Is this element a drawn grouper outline rather than an ordinary space? */
export function isGrouperElement(el: EditorElement): boolean {
    if (el.kind !== 'space') return false
    return isGrouperTypeCode(elementTypeCode(el))
}

/**
 * The **deepest** drawn grouper outline containing a point (spec 2026-08-30-01
 * §6): a space drawn inside a flat's outline joins that flat, the way an
 * equipment picks up the room it was dropped in. Deepest = smallest area, for
 * the same reason `containingSpace` reads it off the geometry — a common area
 * drawn inside a unit is a legitimate nesting and the inner one wins.
 *
 * `excludeKey` keeps a grouper from containing itself while it is being drawn.
 */
export function containingGrouper(doc: SceneDoc, p: Point, excludeKey?: string): SpaceElement | undefined {
    let best: SpaceElement | undefined
    let bestArea = Number.POSITIVE_INFINITY
    for (const el of doc.elements) {
        if (el.kind !== 'space' || el.key === excludeKey) continue
        if (!isGrouperElement(el) || el.boundary.length < 3) continue
        if (!pointInSpace(p, el)) continue
        const area = shoelace(el.boundary)
        if (area < bestArea) {
            best = el
            bestArea = area
        }
    }
    return best
}

/**
 * Every drawn grouper whose outline contains the point, **smallest first**
 * (spec 2026-08-30-03 §6, §7).
 *
 * The order is the answer to both questions the click asks: which one to select
 * (the first — a common area drawn inside a lot is the more specific answer),
 * and what to walk through when the click is ambiguous. Area, not draw order,
 * because the user is pointing at a place, not at a history.
 */
export function groupersAt(doc: SceneDoc, p: Point, toleranceMm = 0): SpaceElement[] {
    const hits: { el: SpaceElement; area: number }[] = []
    for (const el of doc.elements) {
        if (el.kind !== 'space' || !isGrouperElement(el) || el.boundary.length < 3) continue
        // Inside counts, and so does *near the edge*: the click snaps to an
        // outline the way a drawn point snaps to a corner, so aiming at a lot's
        // border catches it instead of falling through to the floor.
        if (!pointInSpace(p, el) && distanceToSpaceEdges(p, el) > toleranceMm) continue
        hits.push({ el, area: shoelace(el.boundary) })
    }
    return hits.sort((a, b) => a.area - b.area).map((h) => h.el)
}

/**
 * The drawn space nearest a point, within `maxDistanceMm` (spec
 * 2026-08-30-03 §17).
 *
 * `containingSpace` cannot answer for an opening: a window's centre sits *on*
 * the wall, which is the room's own boundary, so point-in-polygon is a coin
 * toss decided by how the room happened to be traced. Distance is the honest
 * measure there — inside counts as zero, and a boundary a hair away counts as
 * near.
 *
 * Groupers are not candidates: a lot is not the space a window looks out of.
 */
export function nearestSpace(doc: SceneDoc, p: Point, maxDistanceMm: number): SpaceElement | undefined {
    let best: SpaceElement | undefined
    let bestD = maxDistanceMm
    for (const el of doc.elements) {
        if (el.kind !== 'space' || isGrouperElement(el) || el.boundary.length < 3) continue
        const d = pointInSpace(p, el) ? 0 : distanceToSpaceEdges(p, el)
        if (d <= bestD) {
            best = el
            bestD = d
        }
    }
    return best
}

/**
 * Shortest distance from a point to a space's edges — its holes' included: the
 * wall around a core of toilets is as much the plateau's edge as its façade,
 * and a door cut into it opens onto the plateau.
 */
function distanceToSpaceEdges(p: Point, el: SpaceElement): number {
    return Math.min(distanceToRing(p, el.boundary), ...spaceHoles(el).map((h) => distanceToRing(p, h)))
}

/** The `GrouperRef` a drawn grouper outline stands for. */
export function grouperRefOf(el: EditorElement): GrouperRef | undefined {
    if (!isGrouperElement(el)) return undefined
    const pending = el.owner.newAsset
    return {
        ...(el.owner.assetUid ? { assetUid: el.owner.assetUid } : {}),
        ...(!el.owner.assetUid && pending ? { assetKey: pending.key } : {}),
        name: el.owner.assetName ?? pending?.name ?? '',
        typeCode: elementTypeCode(el),
    }
}

/**
 * The space element the given element **parents to**, when that space is drawn
 * on this plan — the room a radiator was dropped in, whether that room already
 * exists or is being created by the same save.
 *
 * Deliberately not point-in-polygon: the answer has to be the parent that
 * actually resolved, which is what the inspector shows and what the save will
 * write. Re-deriving it from geometry would light up a room the asset is not
 * filed under — after an override, or for a door anchored on its wall's owner.
 */
export function parentSpaceKeyOf(doc: SceneDoc, el: EditorElement | null): string | null {
    if (!el) return null
    const pendingParentKey = el.owner.newAsset?.parentKey
    if (pendingParentKey) {
        const host = doc.elements.find(
            (e) => e.kind === 'space' && e.owner.newAsset?.key === pendingParentKey,
        )
        return host?.key ?? null
    }
    const parentUid = el.owner.newAsset?.parentUid ?? el.owner.assetParentUid
    if (parentUid) {
        const host = doc.elements.find((e) => e.kind === 'space' && e.owner.assetUid === parentUid)
        if (host && host.key !== el.key) return host.key
    }
    // A space's answer to "and where does this belong?" is its **grouper**, and
    // it deserves the same blink an equipment's room gets (spec 2026-08-30-03
    // §16). Only when the grouper is drawn on this plan: one that exists only
    // in the combobox has nothing to light up.
    if (el.kind === 'space' && !isGrouperElement(el)) {
        const id = el.owner.grouper?.assetUid ?? el.owner.grouper?.assetKey
        if (!id) return null
        const outline = doc.elements.find((e) =>
            e.kind === 'space'
            && isGrouperElement(e)
            && (e.owner.assetUid ?? e.owner.newAsset?.key) === id)
        if (outline && outline.key !== el.key) return outline.key
    }
    return null
}

/** A space's holes, an empty list when it has none. */
export const spaceHoles = (el: SpaceElement): Point[][] => el.holes ?? []

/**
 * Whether a point is *in* a space: inside its boundary and in none of its
 * holes. A click on a pillar is not a click on the room around it, and a
 * radiator dropped in the core of toilets is not filed under the plateau.
 */
export function pointInSpace(p: Point, el: SpaceElement): boolean {
    return pointInPolygon(p, el.boundary) && !spaceHoles(el).some((h) => pointInPolygon(p, h))
}

/** Removes one hole; the field goes with the last one, as if it never had any. */
export function removeHole(el: SpaceElement, index: number): void {
    const kept = spaceHoles(el).filter((_, i) => i !== index)
    if (kept.length) el.holes = kept
    else delete el.holes
}

/**
 * The one number a hole's corner goes by among a space's vertices: the
 * boundary's corners come first, then each hole's in turn. Snapping excludes a
 * dragged corner by it.
 */
export function holeVertexIndex(el: SpaceElement, hole: number, vertex: number): number {
    let i = el.boundary.length
    for (let h = 0; h < hole; h++) i += spaceHoles(el)[h]?.length ?? 0
    return i + vertex
}

/** The space's floor area in squared plan units: its boundary minus its holes. */
export const spaceArea = (el: SpaceElement): number => {
    if (el.boundary.length < 3) return 0
    return Math.max(0, spaceHoles(el).reduce((a, h) => a - shoelace(h), shoelace(el.boundary)))
}

/**
 * Whether `ring` can be one of the space's holes: inside its boundary (touching
 * it is allowed, crossing it is not) and clear of every other hole —
 * `exceptHole` leaves out the one being checked against itself. The server
 * refuses a representation that breaks this (`vector_value.go`), so the editor
 * checks it before a hole is cut and flags it once one is dragged out of shape.
 */
export function holeFits(el: SpaceElement, ring: Point[], exceptHole?: number): boolean {
    if (ring.length < 3 || el.boundary.length < 3) return false
    const eps = ringTolerance(el.boundary)
    if (!ringInside(ring, el.boundary, eps)) return false
    return spaceHoles(el).every((h, i) => i === exceptHole || ringsDisjoint(ring, h, eps))
}

/**
 * The space a freshly traced hole belongs to: the **smallest** one it fits
 * in, by the same depth rule as `containingSpace` — a pillar inside a room
 * that sits inside a lot is the room's. `undefined` when it fits in none.
 */
export function spaceForHole(doc: SceneDoc, ring: Point[]): SpaceElement | undefined {
    let best: SpaceElement | undefined
    let bestArea = Number.POSITIVE_INFINITY
    for (const el of doc.elements) {
        if (el.kind !== 'space' || !holeFits(el, ring)) continue
        const area = shoelace(el.boundary)
        if (area < bestArea) {
            best = el
            bestArea = area
        }
    }
    return best
}

export function elementByKey(doc: SceneDoc, key: string | null | undefined): EditorElement | undefined {
    if (!key) return undefined
    return doc.elements.find((e) => e.key === key)
}

/** Every point of an element, for bbox / fit computations. */
export function elementPoints(el: EditorElement): Point[] {
    switch (el.kind) {
        case 'wall':
            return [el.start, el.end]
        case 'space':
            return el.boundary
        case 'opening':
            return [el.center]
        case 'equipment':
            return [el.position]
        case 'run':
            return el.points
        case 'riser':
            return [el.position]
    }
}

export function documentPoints(doc: SceneDoc): Point[] {
    const pts: Point[] = []
    for (const el of doc.elements) pts.push(...elementPoints(el))
    for (const u of doc.underlays) {
        pts.push(applyTransform(u.transform, { x: 0, y: 0 }))
        pts.push(applyTransform(u.transform, { x: 1, y: u.height }))
    }
    return pts
}

/**
 * Auto-fit transform for a raster plan joining the frame as an underlay
 * (spec 2026-08-22-02 §10, exp's `fitCalibToImage`).
 *
 * An underlay's local frame is the crop's unit width: x ∈ [0,1], y ∈ [0,aspect].
 * The transform's `scale` is therefore the underlay's real-world width in
 * millimetres. With no geometry to fit against we fall back to 20 m across —
 * a plausible floor, and something the user can immediately draw on top of.
 */
export function autoFitUnderlayTransform(
    bb: { x0: number; y0: number; x1: number; y1: number } | null,
    aspect: number,
    margin = 0.92,
): Transform {
    const ratio = aspect > 0 ? aspect : 1
    if (!bb) return { tx: -10000, ty: (-10000 * ratio) / 2, rotation_deg: 0, scale: 20000 }
    const w = Math.max(bb.x1 - bb.x0, 1)
    const h = Math.max(bb.y1 - bb.y0, 1)
    const scale = Math.max(w, h / ratio) / margin
    const cx = (bb.x0 + bb.x1) / 2
    const cy = (bb.y0 + bb.y1) / 2
    return { tx: cx - scale / 2, ty: cy - (scale * ratio) / 2, rotation_deg: 0, scale }
}

/**
 * A best-effort aspect ratio (height ÷ width) for a raster representation that
 * is not part of the scene yet.
 *
 * "Use as underlay" auto-fits *before* any registration exists, so no
 * server-computed height is available and the source page's real shape is
 * unknown here — this assumes A4 portrait. The placement it produces is an
 * explicitly low-confidence starting point (provenance `auto_fit`) that the
 * user calibrates, and the rendered shape is corrected by the scene's
 * `underlay_height` as soon as the registration lands.
 */
export function rasterAspect(value: unknown): number {
    return underlayHeight(undefined, value)
}

/** Points of every element, ignoring underlays — what an auto-fit fits against. */
export function geometryPoints(doc: SceneDoc): Point[] {
    const pts: Point[] = []
    for (const el of doc.elements) pts.push(...elementPoints(el))
    return pts
}

/* ------------------------------------------------- layers & registrations */

/**
 * One calibratable layer: a registration already stored server-side, either a
 * raster underlay or a vector representation. Calibration works on **any** of
 * them (spec 2026-08-22-03 §1) — geometry drawn but not saved yet has no
 * registration to patch and is therefore not listed.
 */
export interface SceneLayer {
    registrationUid: string
    repUid?: string
    kind: 'underlay' | 'vector'
    label: string
    assetUid?: string
    transform: Transform
    elementCount: number
    /** `pdf` or `image` for an underlay (see UnderlayEntry.sourceKind). */
    sourceKind?: string
}

export function sceneLayers(doc: SceneDoc): SceneLayer[] {
    const layers = new Map<string, SceneLayer>()
    for (const u of doc.underlays) {
        if (!u.registrationUid) continue
        layers.set(u.registrationUid, {
            registrationUid: u.registrationUid,
            repUid: u.repUid,
            kind: 'underlay',
            label: u.assetName || u.repUid,
            assetUid: u.assetUid,
            transform: u.transform,
            elementCount: 0,
            ...(u.sourceKind ? { sourceKind: u.sourceKind } : {}),
        })
    }
    for (const el of doc.elements) {
        const uid = el.source.registrationUid
        if (!uid) continue
        const existing = layers.get(uid)
        if (existing) {
            existing.elementCount += 1
            continue
        }
        const owner = el.source.repOwner ?? el.owner
        layers.set(uid, {
            registrationUid: uid,
            repUid: el.source.repUid,
            kind: 'vector',
            label: owner.assetName || owner.assetUid || el.source.localId,
            assetUid: owner.assetUid,
            transform: el.source.transform,
            elementCount: 1,
        })
    }
    return [...layers.values()]
}

/** The composed transform a registration currently places its source with. */
export function layerTransform(doc: SceneDoc, registrationUid: string): Transform | null {
    const underlay = doc.underlays.find((u) => u.registrationUid === registrationUid)
    if (underlay) return underlay.transform
    const el = doc.elements.find((e) => e.source.registrationUid === registrationUid)
    return el ? el.source.transform : null
}

/** Every frame-space point a layer covers — what its bounding box is drawn from. */
export function layerPoints(doc: SceneDoc, registrationUid: string): Point[] {
    const pts: Point[] = []
    for (const el of doc.elements) {
        if (el.source.registrationUid === registrationUid) pts.push(...elementPoints(el))
    }
    for (const u of doc.underlays) {
        if (u.registrationUid !== registrationUid) continue
        for (const corner of [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: u.height }, { x: 0, y: u.height }]) {
            pts.push(applyTransform(u.transform, corner))
        }
    }
    return pts
}

export const layerBBox = (doc: SceneDoc, registrationUid: string): BBox | null =>
    bboxOfPoints(layerPoints(doc, registrationUid))

/** Moves an element's geometry by a similarity — used when a whole layer moves. */
export function transformElement(el: EditorElement, t: Transform): void {
    const scale = t.scale || 1
    switch (el.kind) {
        case 'wall':
            el.start = applyTransform(t, el.start)
            el.end = applyTransform(t, el.end)
            el.thickness *= scale
            break
        case 'space':
            el.boundary = el.boundary.map((p) => applyTransform(t, p))
            if (el.holes) el.holes = el.holes.map((h) => h.map((p) => applyTransform(t, p)))
            break
        case 'opening':
            el.center = applyTransform(t, el.center)
            el.directionDeg = normalizeAngle(el.directionDeg + t.rotation_deg)
            el.width *= scale
            el.wallThickness *= scale
            break
        case 'equipment':
            el.position = applyTransform(t, el.position)
            el.rotationDeg = normalizeAngle(el.rotationDeg + t.rotation_deg)
            el.width *= scale
            el.depth *= scale
            break
        case 'run':
            el.points = el.points.map((p) => applyTransform(t, p))
            break
        case 'riser':
            el.position = applyTransform(t, el.position)
            break
    }
}

/** A run's length in plan units — the sum of its segments. */
export const runLength = (el: RunElement): number =>
    el.points.reduce((sum, p, i) => (i === 0 ? 0 : sum + Math.hypot(p.x - el.points[i - 1].x, p.y - el.points[i - 1].y)), 0)

/** The catalogue entry of a network code, if the organization has one. */
export const networkOf = (doc: Pick<SceneDoc, 'networks'>, code: string): PlanNetwork | undefined =>
    doc.networks?.find((n) => n.code === code)

/**
 * Re-places a whole layer: the geometry moves in frame space and its stored
 * composed transform moves with it, so the representation's **own value is
 * unchanged** — only the registration needs a PATCH (spec §1, §6).
 */
export function applyLayerTransform(
    doc: SceneDoc,
    registrationUid: string,
    next: Transform,
    provenance: RegistrationProvenance,
): boolean {
    const current = layerTransform(doc, registrationUid)
    if (!current) return false
    const delta = deltaTransform(current, next)
    for (const u of doc.underlays) {
        if (u.registrationUid === registrationUid) u.transform = { ...next }
    }
    for (const el of doc.elements) {
        if (el.source.registrationUid !== registrationUid) continue
        transformElement(el, delta)
        el.source.transform = { ...next }
    }
    doc.registrationEdits[registrationUid] = {
        registrationUid,
        base: doc.registrationEdits[registrationUid]?.base ?? { ...current },
        transform: { ...next },
        provenance,
    }
    return true
}

/** Drops a pending registration edit once it has been written server-side. */
export function clearRegistrationEdit(doc: SceneDoc, registrationUid: string): void {
    delete doc.registrationEdits[registrationUid]
}

/* --------------------------------------- promotion / demotion / retarget */

/** Local ids only have to be unique inside one representation. */
function localIdsOfRep(doc: SceneDoc, repUid: string | undefined, exceptKey: string): Set<string> {
    return new Set(
        doc.elements
            .filter((e) => e.key !== exceptKey && e.source.repUid === repUid)
            .map((e) => e.source.localId),
    )
}

function uniqueLocalId(doc: SceneDoc, repUid: string | undefined, el: EditorElement): string {
    const taken = localIdsOfRep(doc, repUid, el.key)
    if (!taken.has(el.source.localId)) return el.source.localId
    return nextLocalId(el.kind, taken)
}

/** The elements sharing an element's representation. */
export const groupMembers = (doc: SceneDoc, el: EditorElement): EditorElement[] => {
    const id = groupIdOf(el)
    return doc.elements.filter((e) => groupIdOf(e) === id)
}

/**
 * A *bundle* is a saved representation holding more than one element — an
 * import, a legacy all-in-one value, a wall bundle carrying its openings. A
 * lone element still owned by the frame asset counts too: it is an all-in-one
 * leftover waiting to be given its own asset.
 */
export function isBundleElement(doc: SceneDoc, el: EditorElement): boolean {
    if (!el.source.repUid) return false
    if (groupMembers(doc, el).length > 1) return true
    return repAssetUidOf(el) === doc.frame.uid
}

/** Promotion applies to everything but walls, which move with "owned by" (§5), and runs and risers, which depict their network by link. */
export const canPromote = (doc: SceneDoc, el: EditorElement): boolean =>
    el.kind !== 'wall' && el.kind !== 'run' && el.kind !== 'riser' && isBundleElement(doc, el)

/**
 * **Promote** (spec §3): the element leaves its bundle and becomes its own
 * representation on a new or linked asset, registered into the same frame with
 * the same placement. Geometry is untouched — the element is already expressed
 * in frame coordinates and its new representation is registered with the
 * identity transform, so it does not move by a millimetre. Only ownership does.
 */
export function promoteElement(doc: SceneDoc, key: string, owner: ElementOwner): boolean {
    const el = elementByKey(doc, key)
    if (!el || !canPromote(doc, el)) return false
    el.owner = owner
    el.source = {
        transform: { ...IDENTITY },
        localId: uniqueLocalId(doc, undefined, el),
    }
    syncOpeningWallRefs(doc)
    return true
}

/** A representation an element can be merged back into (same frame, saved). */
export interface BundleTarget {
    repUid: string
    registrationUid?: string
    transform: Transform
    owner: ElementOwner
    label: string
    elementCount: number
}

export function bundleTargets(doc: SceneDoc, el: EditorElement | null): BundleTarget[] {
    const own = el ? groupIdOf(el) : null
    const targets = new Map<string, BundleTarget>()
    for (const g of groupByRepresentation(doc)) {
        if (!g.repUid || g.id === own) continue
        targets.set(g.repUid, {
            repUid: g.repUid,
            registrationUid: g.elements[0]?.source.registrationUid,
            transform: g.transform,
            owner: g.owner,
            label: g.owner.assetName || g.owner.assetUid || g.repUid,
            elementCount: g.elements.length,
        })
    }
    return [...targets.values()]
}

/**
 * **Demote / merge back** (spec §4): the inverse of promotion. The element is
 * absorbed into a bundle of the same frame; its own per-asset representation is
 * left empty and therefore soft-deleted by the diff. The element keeps pointing
 * at the asset it depicts (`properties.asset_uid` on save), so the SVG id
 * contract survives the merge and the asset itself is never deleted.
 */
export function demoteElement(doc: SceneDoc, key: string, target: BundleTarget): boolean {
    const el = elementByKey(doc, key)
    if (!el || !target.repUid) return false
    el.source = {
        repUid: target.repUid,
        registrationUid: target.registrationUid,
        transform: { ...target.transform },
        repOwner: { ...target.owner },
        localId: uniqueLocalId(doc, target.repUid, el),
    }
    syncOpeningWallRefs(doc)
    return true
}

/**
 * **Re-target a wall** (spec §5): a cross-representation cut/paste inside the
 * same frame — the wall joins the target asset's bundle (its existing one when
 * it has one), and every opening drawn on it follows through `wall_ref`.
 */
export function retargetWall(doc: SceneDoc, key: string, owner: ElementOwner): boolean {
    const el = elementByKey(doc, key)
    if (!el || el.kind !== 'wall' || !owner.assetUid) return false
    const existing = doc.elements.find(
        (e) => e.key !== key && e.kind === 'wall' && e.source.repUid && repAssetUidOf(e) === owner.assetUid,
    )
    el.owner = { ...owner }
    el.source = existing
        ? {
            repUid: existing.source.repUid,
            registrationUid: existing.source.registrationUid,
            transform: { ...existing.source.transform },
            repOwner: existing.source.repOwner ? { ...existing.source.repOwner } : { ...owner },
            localId: uniqueLocalId(doc, existing.source.repUid, el),
        }
        : {
            transform: { ...IDENTITY },
            localId: uniqueLocalId(doc, undefined, el),
        }
    syncOpeningWallRefs(doc)
    return true
}

/**
 * Keeps every opening's cross-representation pointer in step with where its
 * host wall now lives: a wall in the same representation is a local
 * attachment (no ref), a wall elsewhere is a `wall_ref` at that
 * representation's asset.
 */
export function syncOpeningWallRefs(doc: SceneDoc): void {
    for (const el of doc.elements) {
        if (el.kind !== 'opening') continue
        const host = el.wallKey ? elementByKey(doc, el.wallKey) : undefined
        if (!host || host.kind !== 'wall') continue
        if (groupIdOf(host) === groupIdOf(el)) {
            el.wallRef = undefined
            continue
        }
        const hostAsset = repAssetUidOf(host)
        el.wallRef = hostAsset ? { assetUid: hostAsset, wallId: host.source.localId } : undefined
    }
}

/**
 * Fills in the display name and type of assets a bundle merely *depicts*: the
 * scene payload only names the asset owning each representation, so a demoted
 * element comes back with a bare uid until the shell resolves it. Labels only —
 * this never changes what a save writes.
 */
export function hydrateOwnerNames(
    doc: SceneDoc,
    resolve: (uid: string) => { name?: string; typeCode?: string; parentUid?: string; autoName?: boolean } | undefined,
): boolean {
    let changed = false
    for (const el of doc.elements) {
        const uid = el.owner.assetUid
        if (!uid || el.owner.assetName) continue
        const found = resolve(uid)
        if (!found) continue
        el.owner = {
            ...el.owner,
            assetName: found.name,
            ...(found.autoName !== undefined ? { assetAutoName: found.autoName } : {}),
            assetTypeCode: el.owner.assetTypeCode ?? found.typeCode,
            assetParentUid: el.owner.assetParentUid ?? found.parentUid,
        }
        changed = true
    }
    return changed
}

/* ------------------------------------------- shared assets (spec 2026-08-25-04 §5) */

/**
 * One drawing of an asset in this scene — a *footprint*. An asset may carry
 * several: two in the same frame is a duplicate drawing (a validation
 * warning), while footprints in *different* frames are the legitimate
 * multi-layer case — the building's elevator drawn on floors 0–4
 * (spec 2026-08-25-03 §5).
 */
export interface AssetFootprint {
    repUid: string
    registrationUid?: string
    /** True when the entry resolved from the asset hierarchy, with no row. */
    implicit: boolean
    /** Editor keys of the elements this footprint holds, for selection. */
    elementKeys: string[]
}

/**
 * `assetUid → footprints`, computed from what the scene payload loaded.
 *
 * This is the *local* half of sharedness: it sees every drawing of an asset in
 * the frame being edited. Footprints on other layers live in other scenes and
 * are resolved by the shell (which knows how to ask); the two are merged for
 * display. A rep with no elements left (everything deleted) drops out, which is
 * exactly what "no longer drawn on this layer" means.
 */
export function assetFootprints(doc: SceneDoc): Map<string, AssetFootprint[]> {
    const out = new Map<string, AssetFootprint[]>()
    for (const el of doc.elements) {
        const uid = el.owner.assetUid
        const repUid = el.source.repUid
        if (!uid || !repUid) continue
        let list = out.get(uid)
        if (!list) {
            list = []
            out.set(uid, list)
        }
        const existing = list.find((f) => f.repUid === repUid)
        if (existing) {
            existing.elementKeys.push(el.key)
            continue
        }
        list.push({
            repUid,
            registrationUid: el.source.registrationUid,
            implicit: !el.source.registrationUid,
            elementKeys: [el.key],
        })
    }
    return out
}

/** Assets drawn more than once in this frame — the duplicate-layer rule (§4). */
export function duplicateFootprints(doc: SceneDoc): { assetUid: string; footprints: AssetFootprint[] }[] {
    const out: { assetUid: string; footprints: AssetFootprint[] }[] = []
    for (const [assetUid, footprints] of assetFootprints(doc)) {
        if (footprints.length > 1) out.push({ assetUid, footprints })
    }
    return out
}

/* --------------------------------------------- calibration (spec 2026-08-25-04 §3) */

/** The frame's millimetres-per-plan-unit, or null when it is not to scale. */
export const sceneMmPerUnit = (doc: SceneDoc): number | null => mmPerUnit(doc.calibration)

/**
 * Records a calibration change. **Nothing moves** — the value is applied
 * optimistically so every readout updates live, and the pending PATCH is kept
 * so Save (or "apply") can write it to the primary plan's registration.
 */
export function applySceneCalibration(
    doc: SceneDoc,
    registrationUid: string,
    calibration: FrameCalibration | null,
): boolean {
    if (!registrationUid) return false
    const base = doc.calibrationEdit?.base ?? doc.calibration ?? null
    doc.calibration = calibration
    doc.calibrationEdit = { registrationUid, calibration, base }
    return true
}

/** Puts the calibration back where the editor found it (Esc in calibrate mode). */
export function revertSceneCalibration(doc: SceneDoc): void {
    if (!doc.calibrationEdit) return
    doc.calibration = doc.calibrationEdit.base
    doc.calibrationEdit = null
}

/** Drops the pending calibration once it has been written server-side. */
export function clearCalibrationEdit(doc: SceneDoc): void {
    doc.calibrationEdit = null
}

/* ------------------------------------------- parent override (spec 2026-08-25-04 §2/§6) */

/**
 * The inspector's parent override on an asset that does not exist yet.
 *
 * It has to **survive the resolution rule**: the pending asset stops pointing
 * at a pending parent (`parentKey`) and stops being a fallback, so the diff
 * writes the uid the user chose and nothing recomputes it behind their back.
 * `parentOverridden` is the flag that says so.
 */
/**
 * Parent a pending asset to **another pending asset** — the room drawn moments
 * ago, which has no uid to point at yet. `parentUid` keeps the best-known
 * fallback (the room's own parent), exactly as the create-time resolution does,
 * so a caller that ignores `parentKey` still writes something sane.
 */
export function overridePendingParentKey(
    el: EditorElement,
    parentKey: string,
    fallbackParentUid: string | undefined,
): boolean {
    const pending = el.owner.newAsset
    if (!pending || !parentKey || pending.key === parentKey) return false
    pending.parentKey = parentKey
    if (fallbackParentUid) pending.parentUid = fallbackParentUid
    pending.parentFallback = false
    pending.parentOverridden = true
    return true
}

export function overridePendingParent(el: EditorElement, parentUid: string): boolean {
    const pending = el.owner.newAsset
    if (!pending || !parentUid) return false
    pending.parentUid = parentUid
    pending.parentKey = undefined
    pending.parentFallback = false
    pending.parentOverridden = true
    return true
}

/**
 * The same override on an asset that already exists — a real re-parent, which
 * the diff turns into `PATCH /assets/{uid}` and the UI confirms first (§6):
 * the asset's implicit frame follows its parent. Every element depicting the
 * asset moves together, or the diff would see two different parents.
 */
export function overrideExistingParent(doc: SceneDoc, assetUid: string, parentUid: string): boolean {
    if (!assetUid || !parentUid) return false
    let changed = false
    for (const el of doc.elements) {
        if (el.owner.assetUid !== assetUid) continue
        if (el.owner.assetParentUid === parentUid) continue
        // `assetParentEdit` is the mark that this was *asked for*. Without it
        // the diff had to infer a re-parent from `assetParentUid` moving, and
        // that field also moves on its own: `hydrateOwnerNames` fills it in for
        // every asset a bundle merely depicts, as the asset index settles after
        // the baseline was taken. An untouched plan then saved a PATCH per
        // depicted asset, writing back the parent it already had.
        el.owner = { ...el.owner, assetParentUid: parentUid, assetParentEdit: parentUid }
        changed = true
    }
    return changed
}
